import React, { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { User } from '../../types';
import GoogleSignInButton from '../GoogleSignInButton';

interface BetaAuthPageProps {
  mode: 'login' | 'register';
  onAuth: (user: User) => void;
  allowRegister?: boolean;
  lead?: string;
}

const MIN_PASSWORD_LENGTH = 7;

const BetaAuthPage: React.FC<BetaAuthPageProps> = ({
  mode,
  onAuth,
  allowRegister = true,
  lead,
}) => {
  const isRegister = allowRegister && mode === 'register';
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleAuthError = (err: unknown, fallback: string) => {
    const message =
      axios.isAxiosError(err) && err.response?.data?.message
        ? String(err.response.data.message)
        : fallback;
    setError(message);
  };

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setError('');
    setBusy(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/login`,
        { email: email.trim(), password },
        { withCredentials: true },
      );
      onAuth(response.data.user);
    } catch (err) {
      handleAuthError(err, 'Sign in failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleRegister = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name.');
      return;
    }
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters long.`);
      return;
    }
    setError('');
    setBusy(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/register`,
        {
          email: email.trim(),
          password,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          createSession: true,
        },
        { withCredentials: true },
      );
      onAuth(response.data.user);
    } catch (err) {
      handleAuthError(err, 'Registration failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async (idToken: string) => {
    if (busy) return;
    setError('');
    setBusy(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/google`,
        {
          idToken,
          createIfMissing: isRegister,
          createSession: true,
        },
        { withCredentials: true },
      );
      onAuth(response.data.user);
    } catch (err) {
      handleAuthError(err, 'Google Sign-In failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="beta-portal-welcome">
      <div className="beta-portal-card">
        <h1 className="beta-auth-title">{isRegister ? 'Create an account' : 'Log in'}</h1>
        <p className="beta-auth-lead">
          {lead || (isRegister
            ? 'Create a Fluora account, then join the beta from your device list.'
            : 'Sign in to see your devices and join the Fluora Beta.')}
        </p>

        <form onSubmit={isRegister ? handleRegister : handleLogin}>
          {isRegister && (
            <div className="row g-2">
              <div className="col-sm-6">
                <label className="form-label" htmlFor="beta-first-name">
                  First name
                </label>
                <input
                  id="beta-first-name"
                  className="form-control"
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                  autoComplete="given-name"
                  required
                />
              </div>
              <div className="col-sm-6">
                <label className="form-label" htmlFor="beta-last-name">
                  Last name
                </label>
                <input
                  id="beta-last-name"
                  className="form-control"
                  value={lastName}
                  onChange={(event) => setLastName(event.target.value)}
                  autoComplete="family-name"
                  required
                />
              </div>
            </div>
          )}

          <label className="form-label mt-2" htmlFor="beta-email">
            Email
          </label>
          <input
            id="beta-email"
            type="email"
            className="form-control"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
          />

          <label className="form-label mt-2" htmlFor="beta-password">
            Password
          </label>
          <input
            id="beta-password"
            type="password"
            className="form-control"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete={isRegister ? 'new-password' : 'current-password'}
            required
          />

          {error && (
            <div className="alert alert-danger mt-3 mb-0" role="alert">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary w-100 mt-3"
            disabled={busy}
          >
            {busy ? 'Please wait…' : isRegister ? 'Create account' : 'Log in'}
          </button>
        </form>

        <div className="beta-portal-divider">
          <span>or</span>
        </div>

        <GoogleSignInButton onCredential={handleGoogle} disabled={busy} />
        {!isRegister && (
          <p className="small text-muted text-center mt-2 mb-0">
            Google will only sign in an existing Fluora account.
          </p>
        )}

        {allowRegister && (
          <p className="beta-auth-switch">
            {isRegister ? (
              <>
                Already have an account? <Link to="/login">Log in</Link>
              </>
            ) : (
              <>
                Don't have an account? <Link to="/register">Create one here.</Link>
              </>
            )}
          </p>
        )}
      </div>
    </main>
  );
};

export default BetaAuthPage;
